/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IdentificationIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  UserIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import Header from '../components/layout/Header';
import { kycService, walletService } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { KYC, KYCStatus, KYCType, DocumentVerificationStatus, KYCProvider, KYCProvidersResponse, DiditSession } from '../types';

const statusConfig: Record<KYCStatus, { color: string; bgColor: string; icon: React.ElementType; text: string }> = {
  pending: { color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', icon: ClockIcon, text: 'Pending Review' },
  in_progress: { color: 'text-blue-400', bgColor: 'bg-blue-500/20', icon: ArrowPathIcon, text: 'Verification In Progress' },
  verified: { color: 'text-green-400', bgColor: 'bg-green-500/20', icon: CheckCircleIcon, text: 'Verified' },
  rejected: { color: 'text-red-400', bgColor: 'bg-red-500/20', icon: XCircleIcon, text: 'Rejected' },
  expired: { color: 'text-orange-400', bgColor: 'bg-orange-500/20', icon: ArrowPathIcon, text: 'Expired' },
  not_submitted: { color: 'text-gray-400', bgColor: 'bg-gray-700', icon: IdentificationIcon, text: 'Not Submitted' },
};

const getDocVerificationStatusColor = (status: DocumentVerificationStatus): string => {
  switch (status) {
    case 'verified':
      return 'bg-green-500/20 text-green-400';
    case 'pending':
    case 'verifying':
      return 'bg-yellow-500/20 text-yellow-400';
    case 'failed':
    case 'invalid':
      return 'bg-red-500/20 text-red-400';
    case 'not_submitted':
    default:
      return 'bg-gray-700 text-gray-400';
  }
};

const getDocVerificationStatusText = (status: DocumentVerificationStatus): string => {
  switch (status) {
    case 'verified':
      return 'Verified';
    case 'pending':
      return 'Pending';
    case 'verifying':
      return 'Verifying...';
    case 'failed':
      return 'Failed';
    case 'invalid':
      return 'Invalid';
    case 'not_submitted':
    default:
      return 'Not Submitted';
  }
};

const KYCPage: React.FC = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!user?.is_platform_admin;
  const [kycType, setKycType] = useState<KYCType>('individual');
  const [idType, setIdType] = useState<'pan' | 'aadhaar'>('pan');
  const [selectedProvider, setSelectedProvider] = useState<KYCProvider | null>(null);
  const [formData, setFormData] = useState({
    pan_number: '',
    aadhaar_number: '',
    full_name: '',
    date_of_birth: '',
    gst_number: '',
    business_name: '',
    organisation_name: '',
    cin_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pin_code: '',
  });

  // Fetch available KYC providers
  const { data: providersData } = useQuery({
    queryKey: ['kyc-providers'],
    queryFn: () => kycService.getProviders().then((res) => res.data as KYCProvidersResponse),
  });

  const { data: kycData, isLoading } = useQuery({
    queryKey: ['kyc'],
    queryFn: () => kycService.getStatus().then((res) => res.data),
    refetchInterval: (query) => {
      // Auto-refetch every 30 seconds if status is pending or in_progress
      const kyc = query.state.data as KYC | undefined;
      if (kyc?.status === 'pending' || kyc?.status === 'in_progress') {
        return 30000;
      }
      return false;
    },
  });

  const { data: walletData } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletService.get().then((res) => res.data),
  });

  // Holds the tab opened synchronously on click so we can redirect it once the
  // session URL resolves - Safari (and increasingly Chrome/Firefox) block
  // window.open() called from an async onSuccess callback.
  const diditTabRef = useRef<Window | null>(null);

  // Didit session mutation
  const diditSessionMutation = useMutation({
    mutationFn: () => kycService.createDiditSession().then((res) => res.data as DiditSession),
    onSuccess: (session) => {
      if (diditTabRef.current) {
        diditTabRef.current.location.href = session.verification_url;
      } else {
        window.open(session.verification_url, '_blank');
      }
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
      toast.success('Verification session created. Complete the verification in the new tab.');
    },
    onError: (error: any) => {
      diditTabRef.current?.close();
      toast.error(error?.response?.data?.error || 'Failed to create verification session');
    },
  });

  const startDiditSession = () => {
    diditTabRef.current = window.open('', '_blank');
    diditSessionMutation.mutate();
  };

  const submitMutation = useMutation({
    mutationFn: () =>
      kycService.submit({
        kyc_type: kycType,
        full_name: kycType === 'individual' ? formData.full_name : undefined,
        date_of_birth: kycType === 'individual' ? formData.date_of_birth : undefined,
        pan_number: idType === 'pan' ? formData.pan_number : undefined,
        aadhaar_number: idType === 'aadhaar' ? formData.aadhaar_number : undefined,
        organisation_name: kycType === 'business' ? formData.organisation_name : undefined,
        gst_number: kycType === 'business' ? formData.gst_number : undefined,
        cin_number: kycType === 'business' ? formData.cin_number : undefined,
        address_line1: formData.address_line1,
        address_line2: formData.address_line2 || undefined,
        city: formData.city,
        state: formData.state,
        pin_code: formData.pin_code,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
      toast.success('KYC submitted successfully');
    },
  });

  const upgradeMutation = useMutation({
    mutationFn: () => {
      if (!user?.id) throw new Error('Not authenticated');
      return walletService.upgradeToPostpaid(user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      toast.success('Upgraded to postpaid billing!');
    },
  });

  const kyc = kycData as KYC | undefined;
  const status: KYCStatus = kyc?.status || 'not_submitted';
  const StatusIcon = statusConfig[status].icon;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMutation.mutate();
  };

  // Helper function to render document verification status
  const renderDocumentVerification = (kyc: KYC) => {
    const documents: Array<{
      label: string;
      status: DocumentVerificationStatus;
      verifiedInfo?: string;
      error?: string;
    }> = [];

    if (kyc.pan_number) {
      documents.push({
        label: 'PAN',
        status: kyc.pan_verification_status || 'not_submitted',
        verifiedInfo: kyc.pan_verified_name,
        error: kyc.pan_verification_error,
      });
    }

    if (kyc.aadhaar_number) {
      documents.push({
        label: 'Aadhaar',
        status: kyc.aadhaar_verification_status || 'not_submitted',
        verifiedInfo: kyc.aadhaar_verified_state,
        error: kyc.aadhaar_verification_error,
      });
    }

    if (kyc.gst_number) {
      documents.push({
        label: 'GST',
        status: kyc.gst_verification_status || 'not_submitted',
        verifiedInfo: kyc.gst_verified_business_name,
        error: kyc.gst_verification_error,
      });
    }

    if (kyc.cin_number) {
      documents.push({
        label: 'CIN',
        status: kyc.cin_verification_status || 'not_submitted',
        verifiedInfo: kyc.cin_verified_company_name,
        error: kyc.cin_verification_error,
      });
    }

    if (documents.length === 0) return null;

    return (
      <div className="mt-4 pt-4 border-t border-gray-700">
        <h4 className="text-sm font-medium text-gray-300 mb-3">Document Verification Status</h4>
        <div className="space-y-3">
          {documents.map((doc) => (
            <div key={doc.label} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">{doc.label}</span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded ${getDocVerificationStatusColor(
                    doc.status
                  )}`}
                >
                  {getDocVerificationStatusText(doc.status)}
                </span>
              </div>
              {doc.status === 'verified' && doc.verifiedInfo && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircleIcon className="w-4 h-4" />
                  {doc.verifiedInfo}
                </span>
              )}
              {(doc.status === 'failed' || doc.status === 'invalid') && doc.error && (
                <span className="text-sm text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  {doc.error}
                </span>
              )}
            </div>
          ))}
        </div>
        {(status === 'pending' || status === 'in_progress') && (
          <p className="text-xs text-gray-400 mt-3">
            Documents are being verified automatically. This usually takes a few minutes.
          </p>
        )}
      </div>
    );
  };

  return (
    <div>
      <Header
        title="KYC Verification"
        subtitle="Complete verification to unlock postpaid billing"
      />

      <div className="p-6 max-w-3xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Status Card */}
            <div className="card mb-6">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-xl ${statusConfig[status].bgColor} ${statusConfig[status].color}`}>
                  <StatusIcon className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">
                    KYC Status: {statusConfig[status].text}
                  </h3>
                  {status === 'verified' && (
                    <p className="text-green-600">
                      {isAdmin
                        ? 'Your KYC has been verified. You can now upgrade to postpaid billing.'
                        : 'Your KYC has been verified. Contact support to enable postpaid billing.'}
                    </p>
                  )}
                  {status === 'pending' && (
                    <div>
                      <p className="text-yellow-600">
                        Your KYC is under review. Documents are being verified automatically.
                      </p>
                      <p className="text-gray-400 text-sm mt-2">
                        If verification is taking longer than expected, please contact us at{' '}
                        <a href="mailto:support@podstack.ai" className="text-primary-400 hover:text-primary-300 underline">
                          support@podstack.ai
                        </a>
                      </p>
                    </div>
                  )}
                  {status === 'in_progress' && (
                    <div>
                      <p className="text-blue-600">
                        Document verification is in progress. This usually takes a few minutes.
                      </p>
                      <p className="text-gray-400 text-sm mt-2">
                        If verification is taking longer than expected, please contact us at{' '}
                        <a href="mailto:support@podstack.ai" className="text-primary-400 hover:text-primary-300 underline">
                          support@podstack.ai
                        </a>
                      </p>
                    </div>
                  )}
                  {status === 'rejected' && (kyc?.rejection_reason || kyc?.reject_reason) && (
                    <p className="text-red-600">
                      Reason: {kyc.rejection_reason || kyc.reject_reason}
                    </p>
                  )}
                  {status === 'expired' && (
                    <p className="text-orange-600">
                      Your KYC has expired. Please resubmit your documents for verification.
                    </p>
                  )}
                  {status === 'not_submitted' && (
                    <p className="text-gray-400">
                      Complete KYC to provision resources on the platform.
                    </p>
                  )}
                </div>
                {status === 'verified' && walletData?.account_type === 'prepaid' && isAdmin && (
                  <button
                    onClick={() => upgradeMutation.mutate()}
                    disabled={upgradeMutation.isPending}
                    className="btn-primary"
                  >
                    {upgradeMutation.isPending ? 'Upgrading...' : 'Upgrade to Postpaid'}
                  </button>
                )}
              </div>

              {/* Didit Verification Status */}
              {kyc?.didit_session_id && (status === 'pending' || status === 'in_progress') && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">ID Verification Status</h4>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GlobeAltIcon className="w-5 h-5 text-primary-400" />
                      <span className="text-sm text-gray-400">Didit ID Verification</span>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      kyc.didit_status === 'Approved' ? 'bg-green-500/20 text-green-400' :
                      kyc.didit_status === 'Declined' ? 'bg-red-500/20 text-red-400' :
                      kyc.didit_status === 'In Progress' || kyc.didit_status === 'In Review' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {kyc.didit_status || 'Not Started'}
                    </span>
                  </div>
                  {(kyc.didit_status === 'Not Started' || kyc.didit_status === 'Abandoned' || kyc.didit_status === 'Expired') && (
                    <button
                      onClick={startDiditSession}
                      disabled={diditSessionMutation.isPending}
                      className="mt-3 btn-secondary text-sm"
                    >
                      {diditSessionMutation.isPending ? 'Creating Session...' : 'Resume Verification'}
                    </button>
                  )}
                  {kyc.didit_status === 'In Review' && (
                    <div className="mt-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <p className="text-sm text-amber-200">
                        Your verification is being reviewed. If it's taking longer than expected, please contact us at{' '}
                        <a href="mailto:support@podstack.ai" className="text-amber-400 hover:text-amber-300 underline font-medium">
                          support@podstack.ai
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Document Verification Status */}
              {kyc && (status === 'pending' || status === 'in_progress' || status === 'verified') && renderDocumentVerification(kyc)}
            </div>

            {/* KYC Form */}
            {(status === 'not_submitted' || status === 'rejected' || status === 'expired') && (
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-6">
                  {status === 'not_submitted' ? 'Submit KYC' : 'Resubmit KYC'}
                </h3>

                {/* Provider Selection - Only show if multiple providers available */}
                {providersData && providersData.providers && providersData.providers.length > 1 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Choose Verification Method
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      {providersData.providers.includes('didit') && (
                        <button
                          type="button"
                          onClick={() => setSelectedProvider('didit')}
                          className={`p-4 border-2 rounded-xl text-left ${
                            selectedProvider === 'didit'
                              ? 'border-primary-500 bg-primary-500/10'
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <GlobeAltIcon className="w-6 h-6 text-primary-500 mb-2" />
                          <h4 className="font-semibold text-white">ID Verification</h4>
                          <p className="text-sm text-gray-400">Verify with passport, ID card, or driver's license</p>
                        </button>
                      )}
                      {providersData.providers.includes('quickekyc') && (
                        <button
                          type="button"
                          onClick={() => setSelectedProvider('quickekyc')}
                          className={`p-4 border-2 rounded-xl text-left ${
                            selectedProvider === 'quickekyc'
                              ? 'border-primary-500 bg-primary-500/10'
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <DocumentCheckIcon className="w-6 h-6 text-primary-500 mb-2" />
                          <h4 className="font-semibold text-white">Document Verification</h4>
                          <p className="text-sm text-gray-400">Verify with PAN, Aadhaar, GST, or CIN</p>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Didit Verification Flow */}
                {(selectedProvider === 'didit' || (providersData?.providers?.length === 1 && providersData.providers[0] === 'didit')) && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <GlobeAltIcon className="w-8 h-8 text-primary-500" />
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2">ID Document Verification</h4>
                    <p className="text-gray-400 mb-6 max-w-md mx-auto">
                      Complete verification by scanning your ID document (passport, national ID, or driver's license) and taking a selfie for liveness check.
                    </p>
                    <button
                      onClick={startDiditSession}
                      disabled={diditSessionMutation.isPending}
                      className="btn-primary px-8 py-3"
                    >
                      {diditSessionMutation.isPending ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                          Creating Session...
                        </>
                      ) : (
                        'Start Verification'
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-4">
                      You will be redirected to complete verification in a secure window
                    </p>
                  </div>
                )}

                {/* QuickeKYC Verification Flow - Only show if QuickeKYC is selected or is the only provider */}
                {(selectedProvider === 'quickekyc' || (providersData?.providers?.length === 1 && providersData.providers[0] === 'quickekyc') || (!selectedProvider && (!providersData || !providersData.providers || providersData.providers.length === 0))) && (
                  <>
                {/* KYC Type Selection */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setKycType('individual')}
                    className={`p-4 border-2 rounded-xl text-left ${kycType === 'individual'
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-gray-700 hover:border-gray-600'
                      }`}
                  >
                    <UserIcon className="w-6 h-6 text-primary-500 mb-2" />
                    <h4 className="font-semibold text-white">Individual</h4>
                    <p className="text-sm text-gray-400">Personal account</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setKycType('business')}
                    className={`p-4 border-2 rounded-xl text-left ${kycType === 'business'
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-gray-700 hover:border-gray-600'
                      }`}
                  >
                    <BuildingOfficeIcon className="w-6 h-6 text-primary-500 mb-2" />
                    <h4 className="font-semibold text-white">Business</h4>
                    <p className="text-sm text-gray-400">Company or startup</p>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Individual Fields */}
                  {kycType === 'individual' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={formData.full_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              full_name: e.target.value,
                            })
                          }
                          className="input"
                          placeholder="John Doe"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_of_birth: e.target.value,
                            })
                          }
                          className="input"
                          required
                        />
                      </div>

                      {/* ID Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          ID Proof Type
                        </label>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <button
                            type="button"
                            onClick={() => setIdType('pan')}
                            className={`py-2 px-4 border-2 rounded-lg font-medium ${idType === 'pan'
                                ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                              }`}
                          >
                            PAN Card
                          </button>
                          <button
                            type="button"
                            onClick={() => setIdType('aadhaar')}
                            className={`py-2 px-4 border-2 rounded-lg font-medium ${idType === 'aadhaar'
                                ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                              }`}
                          >
                            Aadhaar Card
                          </button>
                        </div>
                      </div>

                      {idType === 'pan' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            PAN Number
                          </label>
                          <input
                            type="text"
                            value={formData.pan_number}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                pan_number: e.target.value.toUpperCase(),
                              })
                            }
                            className="input"
                            placeholder="ABCDE1234F"
                            pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                            title="Enter a valid PAN number"
                            required
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Aadhaar Number
                          </label>
                          <input
                            type="text"
                            value={formData.aadhaar_number}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                aadhaar_number: e.target.value.replace(/\D/g, ''),
                              })
                            }
                            className="input"
                            placeholder="1234 5678 9012"
                            pattern="[0-9]{12}"
                            maxLength={12}
                            title="Enter a valid 12-digit Aadhaar number"
                            required
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Business Fields */}
                  {kycType === 'business' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Organisation Name
                        </label>
                        <input
                          type="text"
                          value={formData.organisation_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              organisation_name: e.target.value,
                            })
                          }
                          className="input"
                          placeholder="Acme Inc."
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          GST Number (Optional)
                        </label>
                        <input
                          type="text"
                          value={formData.gst_number}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gst_number: e.target.value.toUpperCase(),
                            })
                          }
                          className="input"
                          placeholder="22AAAAA0000A1Z5"
                          pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}"
                          title="Enter a valid GST number"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          CIN Number (Optional)
                        </label>
                        <input
                          type="text"
                          value={formData.cin_number}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              cin_number: e.target.value.toUpperCase(),
                            })
                          }
                          className="input"
                          placeholder="U12345KA2020PTC123456"
                          title="Enter a valid CIN number"
                        />
                      </div>
                    </>
                  )}

                  {/* Address Fields */}
                  <div className="pt-2">
                    <h4 className="text-sm font-semibold text-white mb-3">Address Details</h4>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Address Line 1
                        </label>
                        <input
                          type="text"
                          value={formData.address_line1}
                          onChange={(e) =>
                            setFormData({ ...formData, address_line1: e.target.value })
                          }
                          className="input"
                          placeholder="House/Flat No., Building Name, Street"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Address Line 2 (Optional)
                        </label>
                        <input
                          type="text"
                          value={formData.address_line2}
                          onChange={(e) =>
                            setFormData({ ...formData, address_line2: e.target.value })
                          }
                          className="input"
                          placeholder="Locality, Landmark"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            City
                          </label>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={(e) =>
                              setFormData({ ...formData, city: e.target.value })
                            }
                            className="input"
                            placeholder="Mumbai"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            value={formData.state}
                            onChange={(e) =>
                              setFormData({ ...formData, state: e.target.value })
                            }
                            className="input"
                            placeholder="Maharashtra"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          PIN Code
                        </label>
                        <input
                          type="text"
                          value={formData.pin_code}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              pin_code: e.target.value.replace(/\D/g, ''),
                            })
                          }
                          className="input"
                          placeholder="400001"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          title="Enter a valid 6-digit PIN code"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      className="btn-primary w-full py-3"
                      disabled={submitMutation.isPending}
                    >
                      {submitMutation.isPending ? 'Submitting...' : 'Submit KYC'}
                    </button>
                  </div>
                </form>
                  </>
                )}
              </div>
            )}

            {/* KYC Details (if verified) */}
            {status === 'verified' && kyc && (
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Verified Details
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-800">
                    <span className="text-gray-400">KYC Type</span>
                    <span className="font-medium text-white capitalize">
                      {kyc.kyc_type}
                    </span>
                  </div>

                  {/* Didit Verified Details */}
                  {kyc.didit_verified_name && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">Verified Name (ID)</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{kyc.didit_verified_name}</span>
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                  )}

                  {kyc.didit_verified_dob && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">Date of Birth (ID)</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{kyc.didit_verified_dob}</span>
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                  )}

                  {kyc.didit_verified_phone && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">Phone Number (Verified)</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{kyc.didit_verified_phone}</span>
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                  )}

                  {kyc.didit_verified_at && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">ID Verification Method</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">Didit ID Verification</span>
                        <GlobeAltIcon className="w-4 h-4 text-primary-400" />
                      </div>
                    </div>
                  )}

                  {kyc.full_name && !kyc.didit_verified_name && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">Full Name</span>
                      <span className="font-medium text-white">
                        {kyc.pan_verified_name || kyc.full_name}
                      </span>
                    </div>
                  )}

                  {kyc.pan_number && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">PAN Number</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">
                          {kyc.pan_number}
                        </span>
                        {kyc.pan_verification_status === 'verified' && (
                          <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    </div>
                  )}

                  {kyc.aadhaar_number && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">Aadhaar Number</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">
                          XXXX-XXXX-{kyc.aadhaar_number.slice(-4)}
                        </span>
                        {kyc.aadhaar_verification_status === 'verified' && (
                          <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    </div>
                  )}

                  {kyc.organisation_name && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">Organisation Name</span>
                      <span className="font-medium text-white">
                        {kyc.gst_verified_business_name || kyc.organisation_name}
                      </span>
                    </div>
                  )}

                  {kyc.gst_number && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">GST Number</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">
                          {kyc.gst_number}
                        </span>
                        {kyc.gst_verification_status === 'verified' && (
                          <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    </div>
                  )}

                  {kyc.cin_number && (
                    <div className="flex justify-between py-2 border-b border-gray-800">
                      <span className="text-gray-400">CIN Number</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">
                          {kyc.cin_number}
                        </span>
                        {kyc.cin_verification_status === 'verified' && (
                          <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between py-2">
                    <span className="text-gray-400">Address</span>
                    <span className="font-medium text-gray-300 text-right max-w-xs">
                      {[kyc.address_line1, kyc.address_line2, kyc.city, kyc.state, kyc.pin_code]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-400" />
                </div>
                <h4 className="font-semibold text-white mb-1">
                  Postpaid Billing
                </h4>
                <p className="text-sm text-gray-400">
                  Pay monthly invoices instead of prepaid
                </p>
              </div>
              <div className="card text-center">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <ArrowPathIcon className="w-6 h-6 text-blue-400" />
                </div>
                <h4 className="font-semibold text-white mb-1">
                  Auto-Pay
                </h4>
                <p className="text-sm text-gray-400">
                  Set up automatic invoice payments
                </p>
              </div>
              <div className="card text-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <IdentificationIcon className="w-6 h-6 text-purple-400" />
                </div>
                <h4 className="font-semibold text-white mb-1">
                  Higher Limits
                </h4>
                <p className="text-sm text-gray-400">
                  Access to higher credit limits
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KYCPage;
